import { useAuth, useAuthStore } from "@dutyon/apis";
import {
  AppShell,
  Box,
  Burger,
  Center,
  Group,
  Loader,
  NavLink,
  Stack,
  Text,
  useDisclosure,
} from "@dutyon/ui-kit";
import {
  BarChart3,
  Building,
  ClipboardCheck,
  ClipboardList,
  FileCheck,
  FileText,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Users,
} from "lucide-react";
import { Suspense } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

const menuGroups = [
  {
    label: "기본 메뉴",
    icon: LayoutDashboard,
    children: [
      { label: "대시보드", icon: LayoutDashboard, to: "/home" },
      { label: "근무 요청", icon: ClipboardList, to: "/duty-requests" },
    ],
  },
  {
    label: "통계 및 분석",
    icon: BarChart3,
    children: [
      { label: "사용자 성장·활동 통계", icon: Users, to: "/statistics/user-trends" },
      { label: "Duty 자동 생성 통계", icon: BarChart3, to: "/statistics/duty-generation" },
      { label: "Duty 요청·응답률 통계", icon: ClipboardCheck, to: "/statistics/duty-requests" },
    ],
  },
  {
    label: "공지 및 문의",
    icon: Megaphone,
    children: [
      { label: "앱 공지사항", icon: Megaphone, to: "/notices/app" },
      { label: "대시보드 공지사항", icon: Megaphone, to: "/notices/dashboard" },
      { label: "문의사항", icon: ClipboardList, to: "/inquiries" },
      { label: "Pro 자료 삭제 요청", icon: ClipboardList, to: "/pro-data-deletion" },
      { label: "Pro 환불 요청", icon: ClipboardList, to: "/pro-billing/refunds" },
    ],
  },
  {
    label: "시스템 관리",
    icon: Building,
    children: [
      { label: "유저 관리", icon: Users, to: "/users" },
      { label: "병원 관리", icon: Building, to: "/companies" },
      { label: "체험하기 신청", icon: FileCheck, to: "/trial-applications" },
      { label: "약관 관리", icon: FileText, to: "/terms" },
      { label: "GPU 운영 상태", icon: LayoutDashboard, to: "/gpu-host" },
      { label: "생성 알림 확인", icon: ClipboardCheck, to: "/generation-notifications" },
    ],
  },
];

export function DashboardLayout() {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const role = useAuthStore((state) => state.user?.role);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <AppShell
      header={{ height: 72 }}
      navbar={{ width: 280, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="lg"
      styles={{
        header: {
          backgroundColor: "rgba(255, 255, 255, 0.94)",
          borderColor: "rgba(226, 232, 240, 0.95)",
          backdropFilter: "blur(12px)",
        },
        navbar: {
          backgroundColor: "#ffffff",
          borderColor: "rgba(226, 232, 240, 0.95)",
        },
        main: {
          background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)",
        },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px={{ base: "sm", md: "lg" }} gap="sm" wrap="nowrap">
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Text fw={900} size="lg" c="blue" style={{ lineHeight: 1.1 }}>
            DutyOn 백오피스
          </Text>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" style={{ display: "flex", flexDirection: "column" }}>
        <Stack gap="sm" style={{ flex: 1, overflowY: "auto" }}>
          {menuGroups.map((group) => (
            <NavLink
              key={group.label}
              label={group.label}
              leftSection={<group.icon size="1rem" strokeWidth={1.5} />}
              opened={true}
              childrenOffset={14}
              styles={{
                root: { borderRadius: 8 },
                label: {
                  color: "var(--mantine-color-dimmed)",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  letterSpacing: 0,
                },
              }}
            >
              {group.children
                .filter(
                  (child) =>
                    ![
                      "/pro-billing/refunds",
                      "/pro-data-deletion",
                      "/gpu-host",
                      "/generation-notifications",
                    ].includes(child.to) || role === "SUPER_ADMIN",
                )
                .map((child) => (
                  <NavLink
                    key={child.to}
                    component={Link}
                    to={child.to}
                    label={child.label}
                    leftSection={<child.icon size="1rem" strokeWidth={1.5} />}
                    active={location.pathname.startsWith(child.to)}
                    variant="light"
                    style={{ borderRadius: 8, minHeight: 42 }}
                  />
                ))}
            </NavLink>
          ))}
        </Stack>
        <NavLink
          label="로그아웃"
          leftSection={<LogOut size="1rem" strokeWidth={1.5} />}
          color="red"
          variant="subtle"
          style={{ borderRadius: 8, minHeight: 42 }}
          onClick={handleLogout}
        />
      </AppShell.Navbar>

      <AppShell.Main style={{ minHeight: "calc(100dvh - 72px)" }}>
        <Box pos="relative" style={{ minHeight: "calc(100dvh - 72px - 2rem)", width: "100%" }}>
          <Suspense
            fallback={
              <Center style={{ minHeight: "calc(100dvh - 120px)", width: "100%" }}>
                <Loader size="lg" />
              </Center>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
